import { useSelector } from 'react-redux';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useFetch } from '@api/hooks';
import type { DashboardPerScopeValueRequest, DashboardPerScopeValueResponse } from '@api/domain/types';
import { DashboardRequestType, DashboardResultOrder } from '@api/domain/types';
import {
  selectDashboardPerScopeValueWorst,
  selectDashboardPerScopeValueBest,
} from '@reducers/v2/dashboard/dashboard.slice';
import {
  fetchDashboardPerScopeValueWorst,
  fetchDashboardPerScopeValueBest,
} from '@reducers/v2/dashboard/thunks';

interface UseGetDashboardPerScopeValueParams extends Omit<DashboardPerScopeValueRequest, '$type'> {
  enabled?: boolean;
}

export const useGetDashboardPerScopeValue = ({
  scope,
  resultType,
  from,
  to,
  orderBy,
  pagination,
  drilldownFilter,
  enabled = true,
}: UseGetDashboardPerScopeValueParams) => {
  const dispatch = useAppDispatch();

  const isWorst = orderBy === DashboardResultOrder.WorstToBest;
  const selector = isWorst ? selectDashboardPerScopeValueWorst : selectDashboardPerScopeValueBest;
  const thunk = isWorst ? fetchDashboardPerScopeValueWorst : fetchDashboardPerScopeValueBest;

  const { data, loading: storeLoading, error } = useSelector(selector);

  const { loading, refetch } = useFetch<DashboardPerScopeValueResponse | null>({
    fetchFn: async (signal) => {
      if (!enabled) return null;
      const result = await dispatch(
        thunk({
          params: {
            $type: DashboardRequestType.PerScopeValue,
            scope,
            resultType,
            from,
            to,
            orderBy,
            pagination,
            drilldownFilter,
          },
          signal,
        })
      ).unwrap();
      return result ?? null;
    },
    deps: [
      enabled,
      scope,
      resultType,
      from,
      to,
      orderBy,
      pagination?.pageNumber,
      pagination?.pageSize,
      pagination?.includeCount,
      drilldownFilter?.$type,
    ],
  });

  return { data, loading: loading || storeLoading, error, refetch };
};
